import { useMutation, useQuery } from '@tanstack/react-query';
import fitStudioRequest from '../utils/request';

const fitMissionServices = {
  // ==================== Mission ====================
  useQueryGetMissionLists({ take, page }, onSuccess) {
    return useQuery(
      ['get-mission', { take, page }],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(`/mission`, {
            params: {
              take: queryKey[1].take,
              page: queryKey[1].page,
            },
          });

          return res.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      {
        onSuccess,
      },
    );
  },
  useQueryGetMissionByUser(
    { take, page, memberCode, memberType, isPartner },
    onSuccess,
  ) {
    return useQuery(
      [
        'get-mission-by-user',
        { take, page, memberCode, memberType, isPartner },
      ],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(`/mission/user`, {
            params: {
              take: queryKey[1].take,
              page: queryKey[1].page,
              memberCode: queryKey[1].memberCode,
              ...(queryKey[1].memberType && {
                memberType: queryKey[1].memberType,
              }),
              ...(queryKey[1].isPartner !== undefined && {
                isPartner: queryKey[1].isPartner,
              }),
            },
          });

          return res.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      {
        onSuccess,
        enabled: !!memberCode,
      },
    );
  },
  useQueryGetMissionById({ missionId }, onSuccess) {
    return useQuery(
      ['get-mission-by-id', { missionId }],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(
            `/mission/${queryKey[1].missionId}`,
          );

          if (res.status === 200) {
            return res.data.data;
          }
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      {
        onSuccess,
        enabled: !!missionId,
        refetchOnWindowFocus: false,
      },
    );
  },
  useMutationCreateMission(onSuccess) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.post(`/mission`, data);

          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      {
        onSuccess,
      },
    );
  },
  useMutationUpdateMission(onSuccess) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.patch(
            `/mission/${data.id}`,
            data.data,
          );

          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      {
        onSuccess,
      },
    );
  },
  useMutationDeleteMission(onSuccess) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.delete(`/mission/${data.id}`);

          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      {
        onSuccess,
      },
    );
  },

  // ==================== Mission Member List ====================
  useQueryGetMissionMemberLists(
    { take, page, missionId, searchText, memberType },
    onSuccess,
  ) {
    return useQuery(
      [
        'get-mission-member-list',
        { take, page, missionId, searchText, memberType },
      ],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(`/mission-member-list`, {
            params: {
              take: queryKey[1].take,
              page: queryKey[1].page,
              missionId: queryKey[1].missionId,
              ...(queryKey[1].searchText && {
                searchText: queryKey[1].searchText,
              }),
              ...(queryKey[1].memberType && {
                memberType: queryKey[1].memberType,
              }),
            },
          });

          return res.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      {
        onSuccess,
        enabled: !!missionId,
      },
    );
  },
  useQueryGetMissionMemberListById({ missionMemberListId }, onSuccess) {
    return useQuery(
      ['get-mission-member-list-by-id', { missionMemberListId }],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(
            `/mission-member-list/${queryKey[1].missionMemberListId}`,
          );

          if (res.status === 200) {
            return res.data.data;
          }
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      {
        onSuccess,
        enabled: !!missionMemberListId,
        refetchOnWindowFocus: false,
      },
    );
  },
  useMutationCreateMissionMemberList(onSuccess) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.post(`/mission-member-list`, data);

          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      {
        onSuccess,
      },
    );
  },
  useMutationUpdateMissionMemberList(onSuccess) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.patch(
            `/mission-member-list/${data.id}`,
            data.data,
          );

          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      {
        onSuccess,
      },
    );
  },
  useMutationDeleteMissionMemberList(onSuccess) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.delete(
            `/mission-member-list/${data.id}`,
          );

          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      {
        onSuccess,
      },
    );
  },
  useMutationUploadMissionMemberListExcel(onSuccess) {
    return useMutation({
      mutationFn: async ({ data, missionId }) => {
        try {
          const res = await fitStudioRequest.post(
            `/mission-member-list/upload-excel/${missionId}`,
            data,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            },
          );

          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      onSuccess,
    });
  },
  useMutationDownloadMissionMemberListExcel(onSuccess) {
    return useMutation({
      mutationFn: async (data) => {
        try {
          const res = await fitStudioRequest.get(
            `/mission-member-list/download-excel/by-filter`,
            {
              params: {
                take: data.take,
                page: data.page,
                missionId: data.missionId,
                ...(data.searchText && { searchText: data.searchText }),
              },
              responseType: 'blob',
            },
          );

          if (res.status === 200) {
            const href = URL.createObjectURL(res.data);
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('download', 'mission-member-list.xlsx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }

          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      onSuccess,
    });
  },

  // ==================== Mission Receivable Member List ====================
  useQueryGetMissionReceivableMemberLists(
    { take, page, missionId, searchText, memberType, status, isReceived },
    onSuccess,
  ) {
    return useQuery(
      [
        'get-mission-receivable-member-list',
        { take, page, missionId, searchText, status, memberType, isReceived },
      ],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(
            `/mission-receivable-member-list`,
            {
              params: {
                take: queryKey[1].take,
                page: queryKey[1].page,
                missionId: queryKey[1].missionId,
                ...(queryKey[1].searchText && {
                  searchText: queryKey[1].searchText,
                }),
                ...(queryKey[1].status && {
                  status: queryKey[1].status,
                }),
                ...(queryKey[1].memberType && {
                  memberType: queryKey[1].memberType,
                }),
                ...(queryKey[1].isReceived !== undefined && {
                  isReceived: queryKey[1].isReceived,
                }),
              },
            },
          );

          return res.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      {
        onSuccess,
        enabled: !!missionId,
      },
    );
  },
  useQueryGetMissionReceivableMemberListById(
    { missionReceivableMemberListId },
    onSuccess,
  ) {
    return useQuery(
      [
        'get-mission-receivable-member-list-by-id',
        { missionReceivableMemberListId },
      ],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(
            `/mission-receivable-member-list/${queryKey[1].missionReceivableMemberListId}`,
          );

          if (res.status === 200) {
            return res.data.data;
          }
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      {
        onSuccess,
        enabled: !!missionReceivableMemberListId,
        refetchOnWindowFocus: false,
      },
    );
  },
  useMutationCreateMissionReceivableMemberList(onSuccess) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.post(
            `/mission-receivable-member-list`,
            data,
          );

          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      {
        onSuccess,
      },
    );
  },
  useMutationUpdateMissionReceivableMemberList(onSuccess) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.patch(
            `/mission-receivable-member-list/${data.id}`,
            data.data,
          );

          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      {
        onSuccess,
      },
    );
  },
  useMutationDeleteMissionReceivableMemberList(onSuccess) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.delete(
            `/mission-receivable-member-list/${data.id}`,
          );

          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      {
        onSuccess,
      },
    );
  },
  useMutationUploadMissionReceivableMemberListExcel(onSuccess) {
    return useMutation({
      mutationFn: async ({ data, missionId }) => {
        try {
          const res = await fitStudioRequest.post(
            `/mission-receivable-member-list/upload-excel/${missionId}`,
            data,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            },
          );

          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      onSuccess,
    });
  },
  useMutationDownloadMissionReceivableMemberListExcel(onSuccess) {
    return useMutation({
      mutationFn: async (data) => {
        try {
          const res = await fitStudioRequest.get(
            `/mission-receivable-member-list/download-excel/by-filter`,
            {
              params: {
                take: data.take,
                page: data.page,
                missionId: data.missionId,
                ...(data.searchText && { searchText: data.searchText }),
                ...(data.status && { status: data.status }),
                ...(data.isReceived !== undefined && {
                  isReceived: data.isReceived,
                }),
              },
              responseType: 'blob',
            },
          );

          if (res.status === 200) {
            const href = URL.createObjectURL(res.data);
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute(
              'download',
              'mission-receivable-member-list.xlsx',
            );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }

          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      onSuccess,
    });
  },

  // ==================== Mission Reward ====================
  useQueryGetMissionRewards({ take, page, searchText, inStock }, onSuccess) {
    return useQuery(
      ['get-mission-reward', { take, page, searchText, inStock }],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(`/mission-reward`, {
            params: {
              take: queryKey[1].take,
              page: queryKey[1].page,
              ...(queryKey[1].searchText && {
                searchText: queryKey[1].searchText,
              }),
              ...(queryKey[1].inStock !== undefined && {
                inStock: queryKey[1].inStock,
              }),
            },
          });

          return res.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      {
        onSuccess,
      },
    );
  },
  useQueryGetMissionRewardById({ missionRewardId }, onSuccess) {
    return useQuery(
      ['get-mission-reward-by-id', { missionRewardId }],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(
            `/mission-reward/${queryKey[1].missionRewardId}`,
          );

          if (res.status === 200) {
            return res.data.data;
          }
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      {
        onSuccess,
        enabled: !!missionRewardId,
        refetchOnWindowFocus: false,
      },
    );
  },
  useMutationCreateMissionReward(onSuccess) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.post(`/mission-reward`, data);

          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      {
        onSuccess,
      },
    );
  },
  useMutationUpdateMissionReward(onSuccess) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.patch(
            `/mission-reward/${data.id}`,
            data.data,
          );

          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      {
        onSuccess,
      },
    );
  },
  useMutationDeleteMissionReward(onSuccess) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.delete(
            `/mission-reward/${data.id}`,
          );

          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      {
        onSuccess,
      },
    );
  },

  // ==================== Mission User Reward ====================
  useQueryGetMissionUserRewards(
    { take, page, searchText, missionRewardId },
    onSuccess,
  ) {
    return useQuery(
      ['get-mission-user-reward', { take, page, searchText, missionRewardId }],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(`/mission-user-reward`, {
            params: {
              take: queryKey[1].take,
              page: queryKey[1].page,
              ...(queryKey[1].searchText && {
                searchText: queryKey[1].searchText,
              }),
              ...(queryKey[1].missionRewardId && {
                missionRewardId: queryKey[1].missionRewardId,
              }),
            },
          });

          return res.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      {
        onSuccess,
      },
    );
  },
  useMutationCreateMissionUserReward(onSuccess) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.post(`/mission-user-reward`, data);

          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      {
        onSuccess,
      },
    );
  },

  // ==================== Mission User ====================
  useQueryGetMissionUserByMemberCode({ memberCode }, onSuccess) {
    return useQuery(
      ['get-mission-user-by-member-code', { memberCode }],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(
            `/mission-user/by-member-code/${queryKey[1].memberCode}`,
          );

          if (res.status === 200) {
            return res.data.data;
          }
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      {
        onSuccess,
        enabled: !!memberCode,
        refetchOnWindowFocus: false,
      },
    );
  },

  // ==================== Mission Point Info ====================
  useQueryGetMissionPointInfoByMissionUserId(
    { missionUserId, month, year },
    onSuccess,
  ) {
    return useQuery(
      [
        'get-mission-point-info-by-mission-user-id',
        { missionUserId, month, year },
      ],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(
            `/mission-point-info/by-mission-user-id`,
            {
              params: {
                missionUserId: queryKey[1].missionUserId,
                month: queryKey[1].month,
                year: queryKey[1].year,
              },
            },
          );

          if (res.status === 200) {
            return res.data.data;
          }
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      {
        onSuccess,
        enabled: !!missionUserId && !!month && !!year,
        refetchOnWindowFocus: false,
      },
    );
  },

  // ==================== Mission Reward Banner ====================
  useQueryGetMissionRewardBanners({ take, page }, onSuccess) {
    return useQuery(
      ['get-mission-reward-banner', { take, page }],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(`/mission-reward-banner`, {
            params: {
              take: queryKey[1].take,
              page: queryKey[1].page,
            },
          });

          return res.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      {
        onSuccess,
      },
    );
  },
  useQueryGetMissionRewardBannerById({ missionRewardBannerId }, onSuccess) {
    return useQuery(
      ['get-mission-reward-banner-by-id', { missionRewardBannerId }],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(
            `/mission-reward-banner/${queryKey[1].missionRewardBannerId}`,
          );

          if (res.status === 200) {
            return res.data.data;
          }
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      {
        onSuccess,
        enabled: !!missionRewardBannerId,
        refetchOnWindowFocus: false,
      },
    );
  },
  useMutationCreateMissionRewardBanner(onSuccess) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.post(
            `/mission-reward-banner`,
            data,
          );

          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      {
        onSuccess,
      },
    );
  },
  useMutationUpdateMissionRewardBannerDisplay(onSuccess) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.patch(
            `/mission-reward-banner/${data.id}/display`,
            { display: data.display },
          );

          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      {
        onSuccess,
      },
    );
  },
  useMutationUpdateMissionRewardBanner(onSuccess) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.patch(
            `/mission-reward-banner/${data.id}`,
            data.data,
          );

          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      {
        onSuccess,
      },
    );
  },
  useMutationDeleteMissionRewardBanner(onSuccess) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.delete(
            `/mission-reward-banner/${data.id}`,
          );

          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      {
        onSuccess,
      },
    );
  },
};

export default fitMissionServices;
